import posthog, { CaptureLogOptions } from "posthog-js";

const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
const posthogHost =
  process.env.REACT_APP_POSTHOG_HOST ?? "https://us.i.posthog.com";
export const isPostHogEnabled = Boolean(posthogKey && posthogHost);

// URLs carry HMAC secrets and tokens; send only origin + route
const URL_PROPERTIES = [
  "$current_url",
  "$pathname",
  "$referrer",
  "$initial_current_url",
  "$initial_pathname",
  "$initial_referrer",
];

const LOG_SOURCE = "caas";

// console capture could forward @mudrex/kyc's auth headers; only ours go out
export const keepOwnLogs = (record: CaptureLogOptions) =>
  record.attributes?.source === LOG_SOURCE ? record : null;

export const sanitizeUrl = (value: unknown) => {
  if (typeof value !== "string") return value;
  const [path] = value.split(/[?#]/);
  return path.replace(/(\/kyc\/digilocker)\/.*/, "$1");
};

const sanitizeProperties = (properties?: Record<string, any>) => {
  if (!properties) return;
  URL_PROPERTIES.forEach((key) => {
    if (key in properties) properties[key] = sanitizeUrl(properties[key]);
  });
};

if (posthogKey && posthogHost) {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    defaults: "2026-05-30",
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    // KYC/payment screens show PAN, Aadhaar, bank details and selfies
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      blockSelector: "img, video, canvas",
    },
    before_send: (event) => {
      if (!event) return event;
      sanitizeProperties(event.properties);
      sanitizeProperties(event.$set);
      sanitizeProperties(event.$set_once);
      return event;
    },
    logs: {
      serviceName: "caas-web-sdk",
      environment: process.env.REACT_APP_STAGE,
      serviceVersion: process.env.REACT_APP_VERSION,
      captureConsoleLogs: false,
      beforeSend: keepOwnLogs,
    },
  });
}

type EventProperties = Record<string, string | number | boolean | undefined>;

export const captureEvent = (name: string, properties: EventProperties = {}) => {
  if (isPostHogEnabled) {
    posthog.capture(name, properties);
    posthog.logger.info(name, { ...properties, source: LOG_SOURCE });
  }
};

export const captureFailure = (
  name: string,
  properties: EventProperties = {}
) => {
  if (isPostHogEnabled) {
    posthog.capture(name, properties);
    posthog.logger.error(name, { ...properties, source: LOG_SOURCE });
  }
};

export default posthog;
